<template>
  <div class="section-container">
    <div class="section-header">
      <h1 class="section-title">學生管理</h1>
      <div class="table-controls">
        <div class="search-add-group">
          <select v-model="statusFilter" class="status-filter">
            <option value="ALL">全部狀態</option>
            <option v-for="status in availableStatuses" :key="status" :value="status">
              {{ getStatusLabel(status) }}
            </option>
          </select>
          <input
            type="text"
            v-model="filterText"
            placeholder="搜索學生..."
            class="search-input"
          />
          <button class="button button-primary" @click="openModal(null)">新增學生</button>
        </div>
      </div>
    </div>

    <div class="table-container">
      <table class="data-table">
        <thead>
          <tr>
            <th @click="sortBy('student_id')">
              ID
              <span v-if="sortColumn === 'student_id'" class="sort-icon">
                {{ sortDirection === 'asc' ? '↑' : '↓' }}
              </span>
            </th>
            <th @click="sortBy('name')">
              姓名
              <span v-if="sortColumn === 'name'" class="sort-icon">
                {{ sortDirection === 'asc' ? '↑' : '↓' }}
              </span>
            </th>
            <th @click="sortBy('status')">
              狀態
              <span v-if="sortColumn === 'status'" class="sort-icon">
                {{ sortDirection === 'asc' ? '↑' : '↓' }}
              </span>
            </th>
            <th @click="sortBy('birthday')">
              生日
              <span v-if="sortColumn === 'birthday'" class="sort-icon">
                {{ sortDirection === 'asc' ? '↑' : '↓' }}
              </span>
            </th>
            <th>操作</th>
          </tr>
        </thead>
        <tbody>
          <tr 
            v-for="student in sortedAndFilteredStudents" 
            :key="student.student_id"
            @click="handleRowClick(student.student_id, $event)"
            class="clickable-row"
          >
            <td>{{ student.student_id }}</td>
            <td>{{ student.name }}</td>
            <td>
              <span :class="`status-badge ${student.status.toLowerCase()}`">
                {{ getStatusLabel(student.status) }}
              </span>
            </td>
            <td>{{ formatDate(student.birthday) }}</td>
            <td class="actions" @click.stop>
              <button class="button button-secondary" @click="openModal(student)">編輯</button>
              <button class="button button-danger" @click="confirmDelete(student.student_id)">刪除</button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <StudentModal
      v-if="showModal"
      :student="currentStudent"
      :isEdit="isEditMode"
      @close="closeModal"
      @saved="handleSaved"
    />
  </div>
</template>

<script setup>
import { ref, computed } from 'vue';
import { useRouter } from 'vue-router';
import { useQuery, useMutation } from '@vue/apollo-composable';
import { GET_STUDENTS, DELETE_STUDENT } from '../graphql/queries';
import StudentModal from './StudentModal.vue';

const router = useRouter();
const { result, refetch } = useQuery(GET_STUDENTS);
const { mutate: deleteStudent } = useMutation(DELETE_STUDENT);

// Modal state
const showModal = ref(false);
const currentStudent = ref(null);
const isEditMode = ref(false);

// Sorting state
const sortColumn = ref('student_id');
const sortDirection = ref('asc');

// Filter state
const filterText = ref('');
const statusFilter = ref('ACTIVE');
const availableStatuses = ['ACTIVE', 'INACTIVE', 'SUSPENDED', 'GRADUATED', 'PROSPECTIVE'];

// Computed properties
const students = computed(() => result.value?.students || []);

// Combined sorted and filtered students
const sortedAndFilteredStudents = computed(() => {
  let filtered = students.value;
  
  // Apply status filter (if not ALL)
  if (statusFilter.value !== 'ALL') {
    filtered = filtered.filter(student => student.status === statusFilter.value);
  }

  // Apply filtering if search text exists
  const searchText = filterText.value.trim().toLowerCase();
  if (searchText) {
    filtered = filtered.filter(student =>
      student.name.toLowerCase().includes(searchText) ||
      getStatusLabel(student.status).toLowerCase().includes(searchText) ||
      student.student_id.toString().includes(searchText)
    );
  }
  
  // Apply sorting
  return [...filtered].sort((a, b) => {
    let valA, valB;
    
    switch (sortColumn.value) {
      case 'student_id':
        valA = parseInt(a.student_id);
        valB = parseInt(b.student_id);
        break;
      case 'birthday':
        valA = a.birthday ? new Date(a.birthday) : null;
        valB = b.birthday ? new Date(b.birthday) : null;
        break;
      case 'status':
        valA = getStatusLabel(a.status);
        valB = getStatusLabel(b.status);
        break;
      default:
        valA = a[sortColumn.value];
        valB = b[sortColumn.value];
    }
    
    // Handle null values for sorting
    if (valA === null || valA === undefined) return 1;
    if (valB === null || valB === undefined) return -1;
    
    if (typeof valA === 'string') {
      valA = valA.toLowerCase();
      valB = valB.toLowerCase();
    }
    
    if (valA < valB) {
      return sortDirection.value === 'asc' ? -1 : 1;
    }
    if (valA > valB) {
      return sortDirection.value === 'asc' ? 1 : -1;
    }
    return 0;
  });
});

const statusLabels = {
  ACTIVE: '正常',
  INACTIVE: '沒有課程',
  SUSPENDED: '暫停',
  GRADUATED: '已畢業',
  PROSPECTIVE: '潛在'
};

// Sorting function
function sortBy(column) {
  if (sortColumn.value === column) {
    sortDirection.value = sortDirection.value === 'asc' ? 'desc' : 'asc';
  } else {
    sortColumn.value = column;
    sortDirection.value = 'asc';
  }
}

function getStatusLabel(status) {
  return statusLabels[status] || status;
}

function formatDate(dateString) {
  if (!dateString) return '-';
  return new Date(dateString).toLocaleDateString();
}

function handleRowClick(studentId, event) {
  const isButtonClick = event.target.closest('button') !== null;
  if (!isButtonClick) {
    router.push(`/students/${studentId}`);
  }
}

function openModal(student) {
  currentStudent.value = student;
  isEditMode.value = !!student;
  showModal.value = true;
}

function closeModal() {
  showModal.value = false;
}

function handleSaved() {
  refetch();
  closeModal();
}

function confirmDelete(studentId) {
  if (confirm('确定要删除这个学生吗？此操作不可撤销。')) {
    deleteStudent({ student_id: studentId }).then(() => {
      refetch();
    });
  }
}
</script>

<style scoped>
.table-container {
  overflow-x: auto;
}

.status-badge {
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 0.8rem;
  color: white;
}

.status-badge.active {
  background-color: var(--primary-color);
}

.status-badge.inactive {
  background-color: var(--text-light);
}

.status-badge.suspended {
  background-color: #FF9800;
}

.status-badge.graduated {
  background-color: var(--secondary-color);
}

.status-badge.prospective {
  background-color: #9C27B0;
}

.actions {
  display: flex;
  gap: 8px;
  cursor: default;
}

.button-danger {
  background-color: var(--danger-color);
  color: white;
}

.button-danger:hover {
  background-color: var(--danger-hover);
}

/* Sorting styles */
th {
  cursor: pointer;
  position: relative;
  user-select: none;
}

th:hover {
  background-color: var(--light-bg);
}

.sort-icon {
  margin-left: 4px;
  font-size: 0.8em;
}

.table-controls {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.search-input {
  padding: 0.5rem 1rem;
  height: 32px;
  border: 1px solid var(--border-color);
  border-radius: 4px;
  font-size: 0.9rem;
  width: 220px;
  transition: all 0.2s ease;
  box-sizing: border-box;
}

.search-input:focus {
  outline: none;
  border-color: var(--primary-color);
  box-shadow: 0 0 0 2px rgba(76, 175, 80, 0.2);
}

.search-add-group {
  display: flex;
  align-items: center;
  gap: 0;
}

.status-filter {
  padding: 0.5rem 0.75rem;
  height: 32px;
  border: 1px solid var(--border-color);
  border-radius: 4px 0 0 4px;
  font-size: 0.9rem;
  background: white;
  color: inherit;
  transition: all 0.2s ease;
  box-sizing: border-box;
  max-width: 140px;
}

.status-filter:focus {
  outline: none;
  border-color: var(--primary-color);
  box-shadow: 0 0 0 2px rgba(76, 175, 80, 0.2);
}

.search-add-group .search-input {
  border-radius: 0;
  border-left: none;
  border-right: none;
}

.search-add-group .button {
  border-radius: 0 4px 4px 0;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .table-controls {
    flex-direction: column;
    align-items: flex-end;
    gap: 0.75rem;
    width: 100%;
  }
  
  .search-add-group {
    width: 100%;
  }
  
  .status-filter {
    max-width: 40%;
  }

  .search-add-group .search-input {
    flex-grow: 1;
    width: auto;
  }
}
</style>